import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';

const trimmedNonEmpty = z
  .string()
  .min(1)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

/**
 * @aiPurpose canonical wire shape for a Road option row (#995). The
 *   directory holds route/corridor labels only — no description,
 *   shortName, or multilingual fields by design.
 * @aiInvariant `name` is trimmed and rejected when blank. DB enforces
 *   active-only normalized uniqueness via a partial unique index on
 *   `lower(name) WHERE active = true` (declared in
 *   `20260522000000_add_road_packaging_dictionaries`); restoring an
 *   archived duplicate while an active duplicate exists surfaces
 *   `DOMAIN_CONFLICT`. List ordering is `(sortOrder ASC, id ASC)`.
 * @aiSecurity none — reference data, no PII surface.
 */
export const RoadOptionSchema = z
  .object({
    id: z.string().uuid(),
    name: trimmedNonEmpty,
    active: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type RoadOption = z.infer<typeof RoadOptionSchema>;

/**
 * @aiPurpose `POST /api/road-options` body. `active` defaults to `true`,
 *   `sortOrder` defaults to `0`.
 */
export const CreateRoadOptionInputSchema = z
  .object({
    name: trimmedNonEmpty,
    active: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
  })
  .strict();
export type CreateRoadOptionInput = z.infer<typeof CreateRoadOptionInputSchema>;

/**
 * @aiPurpose `PATCH /api/road-options/:id` body. `active` is reserved
 *   for the dedicated archive/activate endpoints; the patch shape
 *   intentionally only exposes business fields (`name`, `sortOrder`)
 *   so the lifecycle ledger never collides with field updates.
 */
export const PatchRoadOptionInputSchema = nonEmptyPatch(
  z
    .object({
      name: trimmedNonEmpty,
      sortOrder: z.number().int().min(0),
    })
    .partial()
    .strict(),
);
export type PatchRoadOptionInput = z.infer<typeof PatchRoadOptionInputSchema>;

const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/road-options`. `q` matches
 *   `name` with case-insensitive substring semantics.
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to
 *   `50`, max `500` (matches `arrivalCities` to fit single-round-trip
 *   pickers). `active` defaults to undefined so callers explicitly
 *   opt into the active/archived view.
 */
export const GetRoadOptionsQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    active: stringBoolean.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(50),
  })
  .strict();
export type GetRoadOptionsQuery = z.infer<typeof GetRoadOptionsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/road-options`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying
 *   query returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(sortOrder, id)` of the last returned row.
 */
export const GetRoadOptionsResponseSchema = z
  .object({
    items: z.array(RoadOptionSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetRoadOptionsResponse = z.infer<typeof GetRoadOptionsResponseSchema>;
